/**
 * SHOPIFY STANDARD STOREFRONT EVENTS
 *
 * Dispatches Shopify's standard storefront events so apps and agents can react to
 * commerce interactions without parsing theme DOM or intercepting window.fetch.
 * See: https://shopify.dev/docs/storefronts/themes/best-practices/standard-events
 *
 * Events dispatch from the most specific available element and bubble to document.
 *
 * This module dispatches `shopify:page:view` only. The other events dispatch from the
 * component that owns the interaction, because that is where the data lives:
 * - shopify:cart:lines-update, :note-update, :discount-update, :error
 *   Auto-emitted by the configured Shopify.actions.updateCart action.
 *   See utilities-cart-state-manager.js #configureStandardActions.
 * - shopify:cart:view — cart-drawer.js, when the drawer finishes opening.
 * - shopify:collection:view — product-grid.js, when a grid connects.
 *
 * The library resolves through the `@shopify/events` import map entry declared in
 * snippets/import-map.liquid.
 *
 * This module ALSO centrally listens for every standard event (see
 * STANDARD_EVENT_TYPES below) purely for debug logging, regardless of which
 * component actually dispatched it — so debug output doesn't need to be
 * duplicated at each dispatch site. Gated behind the same `debug` class
 * convention as the rest of the cart system (see allowStandardEventsDebug).
 */

/**
 * Dispatch shopify:page:view on document.
 *
 * Runs inside DOMContentLoaded per Shopify's guidance, so app scripts have time to
 * attach listeners before the event fires. `template` uses request.page_type, which
 * theme-utilities.liquid exposes on window.theme.request.
 *
 * The library is imported dynamically so a CDN failure cannot take the module down
 * with it; a missing analytics event should never surface to the buyer.
 * @returns {Promise<void>}
 */
async function dispatchPageView() {
  try {
    const { PageViewEvent } = await import('@shopify/events');

    document.dispatchEvent(
      new PageViewEvent({
        page: {
          template: window.theme?.request?.page_type || '',
          title: document.title,
          url: window.location.href,
        },
      })
    );
  } catch (error) {
    console.warn('StandardEvents: failed to dispatch shopify:page:view', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', dispatchPageView, { once: true });
} else {
  dispatchPageView();
}

/**
 * Every `shopify:*` standard storefront event this theme dispatches, regardless of
 * which component owns the dispatch (see the file header for the full list of
 * sources). Listened for here, centrally, so debug logging doesn't need to be
 * duplicated at each dispatch site.
 *
 * `shopify:section:*` / `shopify:block:*` are the theme editor's own lifecycle
 * events (see theme-editor.js), not commerce standard events, so they're excluded.
 * @type {string[]}
 */
const STANDARD_EVENT_TYPES = [
  'shopify:page:view',
  'shopify:product:view',
  'shopify:product:select',
  'shopify:collection:view',
  'shopify:search:update',
  'shopify:collection:update',
  'shopify:cart:view',
  'shopify:cart:lines-update',
  'shopify:cart:note-update',
  'shopify:cart:discount-update',
  'shopify:cart:attributes-update',
  'shopify:cart:error',
];

/**
 * Whether standard-event debug logging is enabled.
 *
 * Follows this theme's `classList.contains('debug')` convention (see
 * CartComponentBase#allowDebug), read off `<cart-drawer>` since that is where the
 * flag already gets seeded in Liquid (see sections/cart-drawer.liquid). Checked at
 * dispatch time rather than cached, so toggling the class live takes effect
 * immediately without a reload.
 * @returns {boolean}
 */
function allowStandardEventsDebug() {
  return document.querySelector('cart-drawer')?.classList.contains('debug') ?? false;
}

STANDARD_EVENT_TYPES.forEach((type) => {
  document.addEventListener(type, (event) => {
    if (!allowStandardEventsDebug()) return;

    console.log(`StandardEvents: ${type}`, {
      target: event.target,
      detail: event.detail,
      event,
    });
  });
});
